import React from "react";
import PropTypes from "prop-types";

// Shared allocation-cell rendering, extracted so the Sources table (SourceList) can reuse it
// without pulling in the heavier matrix component.

const STRENGTH_META = {
  high: { dot: "bg-success", text: "", cls: "" },
  medium: { dot: "bg-warning", text: "Medium", cls: "text-warning" },
  low: { dot: "bg-danger", text: "Low", cls: "text-danger" },
};

// Rule strength as a quiet leading dot rather than a saturated pill: teal = high (no label —
// the healthy norm stays calm), amber = medium, red = low (these carry a word because they're
// the exceptions worth noticing). Full explanation stays in the tooltip.
function StrengthDot({ label }) {
  const capitalised = label.charAt(0).toUpperCase() + label.slice(1);
  const meta = STRENGTH_META[label] || { dot: "bg-text-3", text: capitalised, cls: "text-text-2" };
  return (
    <span
      className="inline-flex items-center gap-1 mt-0.5"
      data-tooltip-id="instantTooltip"
      data-tooltip-header="Rule strength"
      data-tooltip-content="How strong the allocation rule is — direct rules score highest, proportional and fixed rules lower. High ≥ 80% · Medium ≥ 55% · Low otherwise."
    >
      <span className={`block w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden />
      {meta.text && <span className={`text-[11px] leading-none ${meta.cls}`}>{meta.text}</span>}
      <span className="sr-only">Rule strength: {capitalised}</span>
    </span>
  );
}
StrengthDot.propTypes = { label: PropTypes.string };

export function DimensionCell({ cell, source, dimensionId, onCellClick }) {
  if (!cell) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onCellClick(source, dimensionId);
        }}
        className="text-xs rounded-ctrl px-2 py-0.5 cursor-pointer text-text-3 hover:text-text focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        aria-label="Add rule"
      >
        {/* Quiet at rest (a faint dash); the "+ rule" affordance surfaces on row hover/focus so
            the empty grid reads as data instead of a wall of dashed boxes. The button stays
            fully operable (labelled, clickable, focusable) regardless of the visual reveal. */}
        <span className="text-text-3 group-hover:hidden group-focus-within:hidden" aria-hidden>—</span>
        <span className="hidden group-hover:inline group-focus-within:inline" aria-hidden>+ rule</span>
      </button>
    );
  }

  const { ownerName, confidenceLabel, owners, allocatedPct } = cell;
  // A multi-owner percentage split: list each owner with its % and surface the unallocated
  // remainder, so the matrix shows the whole split at a glance, not just the primary owner.
  const isMultiSplit = Array.isArray(owners) && owners.length > 1;
  const remainingPct = allocatedPct != null ? Math.max(0, Math.round(100 - allocatedPct)) : 0;

  if (isMultiSplit) {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onCellClick(source, dimensionId);
        }}
        className="flex flex-col gap-0.5 text-left cursor-pointer bg-transparent border-0 p-0"
        aria-label={`Edit split for ${source.displayName || "source"}`}
      >
        {owners.map((o) => (
          <span key={o.ownerNodeId} className="text-xs text-text flex items-center gap-1">
            <span className="font-medium truncate max-w-[120px]">{o.ownerName}</span>
            {o.pct != null && <span className="tabular-nums text-text-2">{Math.round(o.pct)}%</span>}
          </span>
        ))}
        {remainingPct > 0 && (
          <span className="text-[11px] text-warning tabular-nums">{remainingPct}% unallocated</span>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onCellClick(source, dimensionId);
      }}
      className="flex flex-col gap-0.5 text-left cursor-pointer bg-transparent border-0 p-0"
      aria-label={`Edit rule for ${ownerName}`}
    >
      <span className="text-sm font-medium text-text">
        {ownerName}
        {/* Single fixed-% owner that doesn't cover the whole cell — show its share + remainder. */}
        {!isMultiSplit && cell.ruleType === "fixed" && allocatedPct != null && allocatedPct < 100 && (
          <span className="ml-1 text-xs text-text-2 font-normal tabular-nums">
            {Math.round(allocatedPct)}%
          </span>
        )}
      </span>
      {!isMultiSplit && cell.ruleType === "fixed" && remainingPct > 0 && (
        <span className="text-[11px] text-warning tabular-nums">{remainingPct}% unallocated</span>
      )}
      {confidenceLabel && <StrengthDot label={confidenceLabel} />}
    </button>
  );
}
DimensionCell.propTypes = {
  cell: PropTypes.object,
  source: PropTypes.object,
  dimensionId: PropTypes.number,
  onCellClick: PropTypes.func,
};

export function isFullyHighConfidence(source, dimensions) {
  return dimensions.every((dim) => {
    const cell = source.cells?.[dim.id];
    return cell && cell.confidenceLabel === "high";
  });
}
